/**
 * Wall clock ↔ UTC for a tournament's IANA timezone (spec §10).
 *
 * Games store UTC ISO-8601, but an admin schedules in the event's local time and
 * `<input type="datetime-local">` speaks the *browser's* clock. Converting through
 * these helpers keeps a director in one timezone from writing games offset into
 * another. There is no date library here, so the offset is solved with `Intl`.
 */

const PARTS: Intl.DateTimeFormatOptions = {
  hour12: false,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
};

function partsIn(utcMs: number, timeZone: string): Record<string, string> {
  const formatted = new Intl.DateTimeFormat("en-US", { timeZone, ...PARTS }).formatToParts(
    new Date(utcMs),
  );
  return Object.fromEntries(formatted.map((p) => [p.type, p.value]));
}

/** Offset in ms of `timeZone` from UTC at the given instant. */
function tzOffsetMs(utcMs: number, timeZone: string): number {
  const p = partsIn(utcMs, timeZone);
  // Some engines format midnight as hour "24"; fold it back to 0.
  const asIfUtc = Date.UTC(
    Number(p.year),
    Number(p.month) - 1,
    Number(p.day),
    Number(p.hour) % 24,
    Number(p.minute),
    Number(p.second),
  );
  return asIfUtc - utcMs;
}

/**
 * "2026-07-25T09:00" read as wall clock in `timeZone` → "2026-07-25T14:00:00.000Z".
 * Returns null when the input is not a usable datetime-local value.
 *
 * During the ambiguous hour when clocks fall back, a wall clock maps to two
 * instants; this resolves to the first (daylight) occurrence.
 */
export function wallClockToIso(wall: string, timeZone: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(wall);
  if (!m) return null;
  const guess = Date.parse(`${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:00Z`);
  if (Number.isNaN(guess)) return null;
  // Solve twice: the offset at the guessed instant can differ from the offset at
  // the true instant across a DST transition.
  const first = guess - tzOffsetMs(guess, timeZone);
  return new Date(guess - tzOffsetMs(first, timeZone)).toISOString();
}

/** "2026-07-25T14:00:00.000Z" → "2026-07-25T09:00" for a datetime-local input. */
export function isoToWallClock(iso: string | undefined, timeZone: string): string {
  if (!iso) return "";
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return "";
  const p = partsIn(ms, timeZone);
  const hour = String(Number(p.hour) % 24).padStart(2, "0");
  return `${p.year}-${p.month}-${p.day}T${hour}:${p.minute}`;
}

/**
 * How many minutes later (positive) or earlier (negative) the instant `iso`
 * reads on the clock in `toZone` than in `fromZone`. What the Rules card
 * tells an organizer a timezone change would do to the games already on the
 * board (console spec §5.2).
 */
export function wallClockShiftMinutes(iso: string, fromZone: string, toZone: string): number {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return 0;
  return (tzOffsetMs(ms, toZone) - tzOffsetMs(ms, fromZone)) / 60_000;
}
